import { Component, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { ArrayDataSource, ComponentsModule, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { AuthenticationService } from '@lib/auth/authentication.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SettingsService } from '@lib/setting/settings.service';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { ApiTokenCreateResponse, StoreApiTokenRequest, createApiTokenFormGroup } from '@lib/api/models/setting';
import { FormValidationService } from '@shared/services/form-validation.service';
import { ToggleComponent } from '@shared/components/toggle/toggle.component';
import { map } from 'rxjs';

@Component({
    selector: 'app-settings-api-page',
    standalone: true,
    imports: [
        ComponentsModule,
        HeaderComponent,
        FormsModule,
        ReactiveFormsModule,
        ToggleComponent
    ],
    templateUrl: './settings-api-page.component.html',
    styleUrl: './settings-api-page.component.scss'
})
export class SettingsApiPageComponent {
    private readonly _authService = inject(AuthenticationService);
    private readonly _router = inject(Router);
    private readonly _settingsService = inject(SettingsService);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _formValidationService = inject(FormValidationService);

    protected readonly user = toSignal(this._authService.user$);
    protected readonly createTokenFormGroup = createApiTokenFormGroup('External Integration');
    protected createdToken: string | null = null;

    protected previewToken = '';
    protected previewResults: Partial<Record<string, {
        loading: boolean;
        status?: number;
        body?: string;
        error?: string;
        duration?: number;
    }>> = {};

    protected builderName = '';
    protected builderReference = '';
    protected builderEmail = '';
    protected builderQuery = '';
    protected builderPerPage = 100;
    protected readonly builderStatusControl = new FormControl<string>('');
    protected readonly builderSortControl = new FormControl<string>('id');
    protected readonly builderSortDirectionControl = new FormControl<string>('asc');

    protected readonly statusDataSource = new ArrayDataSource({
        items: [
            { label: 'Any', value: '' },
            { label: 'Active', value: '1' },
            { label: 'Inactive', value: '0' },
        ]
    });

    protected readonly sortFieldDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'name', value: 'name' },
            { label: 'email', value: 'email' },
            { label: 'cqc_location_id', value: 'cqc_location_id' },
        ]
    });

    protected readonly sortDirectionDataSource = new ArrayDataSource({
        items: [
            { label: 'asc', value: 'asc' },
            { label: 'desc', value: 'desc' },
        ]
    });

    protected readonly builderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'name', value: 'name' },
            { label: 'email', value: 'email' },
            { label: 'status', value: 'status' },
            { label: 'cqc_location_id', value: 'cqc_location_id' },
        ]
    });

    protected readonly builderColumnsControl = new FormControl<string[]>([
        'id',
        'reference',
        'name',
        'email',
        'status',
        'cqc_location_id',
    ]);

    protected builderRoleDataSource = new ArrayDataSource({
        items: [] as { label: string; value: string }[]
    });

    protected readonly builderRolesControl = new FormControl<string[]>([]);

    protected readonly builderRegionColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'name', value: 'name' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly builderRegionColumnsControl = new FormControl<string[]>(['id', 'reference', 'name', 'status']);

    protected readonly builderZoneColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'name', value: 'name' },
            { label: 'description', value: 'description' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly builderZoneColumnsControl = new FormControl<string[]>(['id', 'name', 'status']);

    protected readonly builderPriorityColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'name', value: 'name' },
            { label: 'description', value: 'description' },
            { label: 'delay', value: 'delay' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly builderPriorityColumnsControl = new FormControl<string[]>(['id', 'name', 'delay', 'status']);
    protected builderIncludeRegions = true;
    protected builderIncludeZones = false;
    protected builderIncludePriorities = false;

    protected permissionBuilderName = '';
    protected permissionBuilderKey = '';
    protected permissionBuilderQuery = '';

    protected usersBuilderReference = '';
    protected usersBuilderEmail = '';
    protected usersBuilderQuery = '';
    protected usersBuilderPerPage = 100;
    protected readonly usersBuilderStatusControl = new FormControl<string>('');
    protected readonly usersBuilderSortControl = new FormControl<string>('id');
    protected readonly usersBuilderSortDirectionControl = new FormControl<string>('asc');
    protected readonly usersBuilderRolesControl = new FormControl<string[]>([]);
    protected readonly usersBuilderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'email', value: 'email' },
            { label: 'status', value: 'status' },
            { label: 'created_at', value: 'created_at' },
            { label: 'updated_at', value: 'updated_at' },
        ]
    });

    protected readonly usersBuilderColumnsControl = new FormControl<string[]>([
        'id',
        'reference',
        'email',
        'status',
        'created_at',
    ]);

    protected commissionersBuilderName = '';
    protected commissionersBuilderReference = '';
    protected commissionersBuilderEmail = '';
    protected commissionersBuilderQuery = '';
    protected commissionersBuilderPerPage = 100;
    protected readonly commissionersBuilderStatusControl = new FormControl<string>('');
    protected readonly commissionersBuilderSortControl = new FormControl<string>('id');
    protected readonly commissionersBuilderSortDirectionControl = new FormControl<string>('asc');
    protected readonly commissionersBuilderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'name', value: 'name' },
            { label: 'email', value: 'email' },
            { label: 'status', value: 'status' },
            { label: 'created_at', value: 'created_at' },
            { label: 'updated_at', value: 'updated_at' },
        ]
    });

    protected readonly commissionersBuilderColumnsControl = new FormControl<string[]>([
        'id',
        'reference',
        'name',
        'email',
        'status',
        'created_at',
    ]);

    protected commissionersBuilderIncludeAddress = true;
    protected commissionersBuilderIncludeUsers = false;
    protected readonly commissionersBuilderAddressColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'street_1', value: 'street_1' },
            { label: 'street_2', value: 'street_2' },
            { label: 'town', value: 'town' },
            { label: 'county', value: 'county' },
            { label: 'post_code', value: 'post_code' },
            { label: 'country', value: 'country' },
            { label: 'lat', value: 'lat' },
            { label: 'lng', value: 'lng' },
        ]
    });

    protected readonly commissionersBuilderAddressColumnsControl = new FormControl<string[]>([
        'id',
        'street_1',
        'town',
        'county',
        'post_code',
        'lat',
        'lng',
    ]);

    protected readonly commissionersBuilderUsersColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'commissioner_id', value: 'commissioner_id' },
            { label: 'user_id', value: 'user_id' },
            { label: 'created_at', value: 'created_at' },
            { label: 'updated_at', value: 'updated_at' },
        ]
    });

    protected readonly commissionersBuilderUsersColumnsControl = new FormControl<string[]>([
        'id',
        'commissioner_id',
        'user_id',
    ]);

    protected regionsBuilderName = '';
    protected regionsBuilderReference = '';
    protected regionsBuilderQuery = '';
    protected regionsBuilderPerPage = 100;
    protected readonly regionsBuilderStatusControl = new FormControl<string>('');
    protected readonly regionsBuilderSortControl = new FormControl<string>('name');
    protected readonly regionsBuilderSortDirectionControl = new FormControl<string>('asc');

    protected readonly regionsBuilderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'name', value: 'name' },
            { label: 'status', value: 'status' },
            { label: 'created_at', value: 'created_at' },
            { label: 'updated_at', value: 'updated_at' },
        ]
    });

    protected readonly regionsBuilderColumnsControl = new FormControl<string[]>([
        'id', 'reference', 'name', 'status',
    ]);

    protected regionsBuilderIncludeProviders = false;

    protected readonly regionsBuilderPriorityColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'name', value: 'name' },
            { label: 'description', value: 'description' },
            { label: 'delay', value: 'delay' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly regionsBuilderPriorityColumnsControl = new FormControl<string[]>([
        'id', 'name', 'delay', 'status',
    ]);

    protected readonly regionsBuilderZoneColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'name', value: 'name' },
            { label: 'description', value: 'description' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly regionsBuilderZoneColumnsControl = new FormControl<string[]>([
        'id', 'name', 'status',
    ]);

    protected readonly regionsBuilderProviderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'name', value: 'name' },
            { label: 'email', value: 'email' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly regionsBuilderProviderColumnsControl = new FormControl<string[]>([
        'id', 'reference', 'name', 'status',
    ]);

    protected careTypesBuilderQuery = '';
    protected careTypesBuilderPerPage = 100;
    protected readonly careTypesBuilderStatusControl = new FormControl<string>('');
    protected readonly careTypesBuilderSortControl = new FormControl<string>('name');
    protected readonly careTypesBuilderSortDirectionControl = new FormControl<string>('asc');

    protected readonly careTypesBuilderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'name', value: 'name' },
            { label: 'description', value: 'description' },
            { label: 'status', value: 'status' },
            { label: 'created_at', value: 'created_at' },
            { label: 'updated_at', value: 'updated_at' },
        ]
    });

    protected readonly careTypesBuilderColumnsControl = new FormControl<string[]>([
        'id', 'name', 'description', 'status',
    ]);

    protected careTypesBuilderIncludeCareOptions = false;

    protected readonly careTypesBuilderCareOptionColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'name', value: 'name' },
            { label: 'description', value: 'description' },
            { label: 'include_price', value: 'include_price' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly careTypesBuilderCareOptionColumnsControl = new FormControl<string[]>([
        'id', 'name', 'include_price', 'status',
    ]);

    protected careOptionsBuilderQuery = '';
    protected careOptionsBuilderCareTypeId = '';
    protected careOptionsBuilderPerPage = 100;
    protected readonly careOptionsBuilderStatusControl = new FormControl<string>('');
    protected readonly careOptionsBuilderIncludePriceControl = new FormControl<string>('');
    protected readonly careOptionsBuilderSortControl = new FormControl<string>('name');
    protected readonly careOptionsBuilderSortDirectionControl = new FormControl<string>('asc');

    protected readonly careOptionsBuilderIncludePriceDataSource = new ArrayDataSource({
        items: [
            { label: 'Any', value: '' },
            { label: 'Yes', value: '1' },
            { label: 'No', value: '0' },
        ]
    });

    protected readonly careOptionsBuilderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'name', value: 'name' },
            { label: 'description', value: 'description' },
            { label: 'include_price', value: 'include_price' },
            { label: 'care_type_id', value: 'care_type_id' },
            { label: 'status', value: 'status' },
            { label: 'created_at', value: 'created_at' },
            { label: 'updated_at', value: 'updated_at' },
        ]
    });

    protected readonly careOptionsBuilderColumnsControl = new FormControl<string[]>([
        'id',
        'name',
        'description',
        'include_price',
        'care_type_id',
        'status',
    ]);

    protected readonly careOptionsBuilderCareTypeColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'name', value: 'name' },
            { label: 'description', value: 'description' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly careOptionsBuilderCareTypeColumnsControl = new FormControl<string[]>([
        'id', 'name', 'status',
    ]);

    protected handbacksBuilderPackageId = '';
    protected handbacksBuilderProviderId = '';
    protected handbacksBuilderHandbackReasonId = '';
    protected handbacksBuilderQuery = '';
    protected handbacksBuilderPerPage = 100;
    protected readonly handbacksBuilderPrivateControl = new FormControl<string>('');
    protected readonly handbacksBuilderSortControl = new FormControl<string>('id');
    protected readonly handbacksBuilderSortDirectionControl = new FormControl<string>('asc');

    protected readonly handbacksBuilderPrivateDataSource = new ArrayDataSource({
        items: [
            { label: 'Any', value: '' },
            { label: 'Private', value: '1' },
            { label: 'Not private', value: '0' },
        ]
    });

    protected readonly handbacksBuilderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'package_id', value: 'package_id' },
            { label: 'provider_id', value: 'provider_id' },
            { label: 'handback_reason_id', value: 'handback_reason_id' },
            { label: 'user_id', value: 'user_id' },
            { label: 'private', value: 'private' },
            { label: 'additional_comments', value: 'additional_comments' },
            { label: 'created_at', value: 'created_at' },
            { label: 'updated_at', value: 'updated_at' },
        ]
    });

    protected readonly handbacksBuilderColumnsControl = new FormControl<string[]>([
        'id',
        'package_id',
        'provider_id',
        'handback_reason_id',
        'private',
        'additional_comments',
        'created_at',
    ]);

    protected handbacksBuilderIncludePackage = false;

    protected readonly handbacksBuilderPackageColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'weekly_hours', value: 'weekly_hours' },
            { label: 'start_date', value: 'start_date' },
            { label: 'end_date', value: 'end_date' },
            { label: 'open', value: 'open' },
            { label: 'commissioner_id', value: 'commissioner_id' },
            { label: 'provider_id', value: 'provider_id' },
            { label: 'created_at', value: 'created_at' },
        ]
    });

    protected readonly handbacksBuilderPackageColumnsControl = new FormControl<string[]>([
        'id', 'reference', 'weekly_hours', 'open', 'commissioner_id',
    ]);

    protected packagesBuilderReference = '';
    protected packagesBuilderCommissionerId = '';
    protected packagesBuilderProviderId = '';
    protected packagesBuilderCareOptionId = '';
    protected packagesBuilderQuery = '';
    protected packagesBuilderPerPage = 100;
    protected readonly packagesBuilderOpenControl = new FormControl<string>('');
    protected readonly packagesBuilderPublishControl = new FormControl<string>('');
    protected readonly packagesBuilderSortControl = new FormControl<string>('id');
    protected readonly packagesBuilderSortDirectionControl = new FormControl<string>('asc');

    protected readonly packagesBuilderOpenDataSource = new ArrayDataSource({
        items: [
            { label: 'Any', value: '' },
            { label: 'Open', value: '1' },
            { label: 'Closed', value: '0' },
        ]
    });

    protected readonly packagesBuilderPublishDataSource = new ArrayDataSource({
        items: [
            { label: 'Any', value: '' },
            { label: 'Published', value: '1' },
            { label: 'Unpublished', value: '0' },
        ]
    });

    protected readonly packagesBuilderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'support_required', value: 'support_required' },
            { label: 'details', value: 'details' },
            { label: 'weekly_hours', value: 'weekly_hours' },
            { label: 'start_date', value: 'start_date' },
            { label: 'end_date', value: 'end_date' },
            { label: 'carers_required', value: 'carers_required' },
            { label: 'open', value: 'open' },
            { label: 'publish', value: 'publish' },
            { label: 'commissioner_id', value: 'commissioner_id' },
            { label: 'provider_id', value: 'provider_id' },
            { label: 'care_option_id', value: 'care_option_id' },
            { label: 'created_at', value: 'created_at' },
            { label: 'updated_at', value: 'updated_at' },
        ]
    });

    protected readonly packagesBuilderColumnsControl = new FormControl<string[]>([
        'id',
        'reference',
        'weekly_hours',
        'start_date',
        'end_date',
        'open',
        'publish',
        'commissioner_id',
        'provider_id',
        'created_at',
    ]);

    protected packagesBuilderIncludeAddress = false;
    protected packagesBuilderIncludeClients = false;
    protected packagesBuilderIncludeCommissioner = false;
    protected packagesBuilderIncludeProvider = false;
    protected packagesBuilderIncludeCareOption = false;

    protected readonly packagesBuilderAddressColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'street_1', value: 'street_1' },
            { label: 'street_2', value: 'street_2' },
            { label: 'town', value: 'town' },
            { label: 'county', value: 'county' },
            { label: 'post_code', value: 'post_code' },
            { label: 'country', value: 'country' },
            { label: 'lat', value: 'lat' },
            { label: 'lng', value: 'lng' },
        ]
    });

    protected readonly packagesBuilderAddressColumnsControl = new FormControl<string[]>([
        'id', 'street_1', 'town', 'county', 'post_code', 'lat', 'lng',
    ]);

    protected readonly packagesBuilderClientsColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'nhs_number', value: 'nhs_number' },
            { label: 'email', value: 'email' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly packagesBuilderClientsColumnsControl = new FormControl<string[]>([
        'id', 'reference', 'nhs_number', 'status',
    ]);

    protected readonly packagesBuilderCommissionerColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'name', value: 'name' },
            { label: 'email', value: 'email' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly packagesBuilderCommissionerColumnsControl = new FormControl<string[]>([
        'id', 'reference', 'name', 'status',
    ]);

    protected readonly packagesBuilderProviderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'name', value: 'name' },
            { label: 'email', value: 'email' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly packagesBuilderProviderColumnsControl = new FormControl<string[]>([
        'id', 'reference', 'name', 'status',
    ]);

    protected readonly packagesBuilderCareOptionColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'name', value: 'name' },
            { label: 'description', value: 'description' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly packagesBuilderCareOptionColumnsControl = new FormControl<string[]>([
        'id', 'name', 'status',
    ]);

    protected clientsBuilderReference = '';
    protected clientsBuilderEmail = '';
    protected clientsBuilderNhsNumber = '';
    protected clientsBuilderQuery = '';
    protected clientsBuilderPerPage = 100;
    protected clientsBuilderCommissionerId = '';
    protected readonly clientsBuilderStatusControl = new FormControl<string>('');
    protected readonly clientsBuilderSortControl = new FormControl<string>('id');
    protected readonly clientsBuilderSortDirectionControl = new FormControl<string>('asc');
    protected readonly clientsBuilderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'nhs_number', value: 'nhs_number' },
            { label: 'email', value: 'email' },
            { label: 'status', value: 'status' },
            { label: 'person_id', value: 'person_id' },
            { label: 'created_at', value: 'created_at' },
            { label: 'updated_at', value: 'updated_at' },
        ]
    });

    protected readonly clientsBuilderColumnsControl = new FormControl<string[]>([
        'id',
        'reference',
        'nhs_number',
        'email',
        'status',
    ]);

    protected clientsBuilderIncludePerson = true;

    protected clientsBuilderIncludePersonAddress = true;

    protected clientsBuilderIncludeAssociatedCommissioners = false;

    protected readonly clientsBuilderPersonColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'title', value: 'title' },
            { label: 'given_name', value: 'given_name' },
            { label: 'middle_names', value: 'middle_names' },
            { label: 'family_name', value: 'family_name' },
            { label: 'date_of_birth', value: 'date_of_birth' },
            { label: 'status', value: 'status' },
            { label: 'address_id', value: 'address_id' },
        ]
    });

    protected readonly clientsBuilderPersonColumnsControl = new FormControl<string[]>([
        'id',
        'given_name',
        'family_name',
        'date_of_birth',
        'address_id',
    ]);

    protected readonly clientsBuilderAddressColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'street_1', value: 'street_1' },
            { label: 'street_2', value: 'street_2' },
            { label: 'town', value: 'town' },
            { label: 'county', value: 'county' },
            { label: 'post_code', value: 'post_code' },
            { label: 'country', value: 'country' },
            { label: 'lat', value: 'lat' },
            { label: 'lng', value: 'lng' },
        ]
    });

    protected readonly clientsBuilderAddressColumnsControl = new FormControl<string[]>([
        'id',
        'street_1',
        'town',
        'county',
        'post_code',
        'lat',
        'lng',
    ]);

    protected readonly clientsBuilderCommissionersColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'name', value: 'name' },
            { label: 'email', value: 'email' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly clientsBuilderCommissionersColumnsControl = new FormControl<string[]>([
        'id',
        'reference',
        'name',
        'status',
    ]);

    protected messageThreadsBuilderSenderId = '';

    protected messageThreadsBuilderSubject = '';

    protected messageThreadsBuilderPerPage = 100;

    protected readonly messageThreadsBuilderSortControl = new FormControl<string>('created_at');

    protected readonly messageThreadsBuilderSortDirectionControl = new FormControl<string>('desc');

    protected readonly messageThreadsBuilderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'subject', value: 'subject' },
            { label: 'sender_id', value: 'sender_id' },
            { label: 'created_at', value: 'created_at' },
            { label: 'updated_at', value: 'updated_at' },
        ]
    });

    protected readonly messageThreadsBuilderColumnsControl = new FormControl<string[]>([
        'id', 'subject', 'sender_id', 'created_at',
    ]);

    protected readonly messageThreadsBuilderSenderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'email', value: 'email' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly messageThreadsBuilderSenderColumnsControl = new FormControl<string[]>([
        'id', 'email',
    ]);

    protected readonly messageThreadsBuilderMessageColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'sender_id', value: 'sender_id' },
            { label: 'content', value: 'content' },
            { label: 'remove_user_indicator', value: 'remove_user_indicator' },
            { label: 'created_at', value: 'created_at' },
            { label: 'updated_at', value: 'updated_at' },
        ]
    });

    protected readonly messageThreadsBuilderMessageColumnsControl = new FormControl<string[]>([
        'id', 'sender_id', 'content', 'created_at',
    ]);

    protected readonly messageThreadsBuilderMessageSenderColumnDataSource = new ArrayDataSource({
        items: [
            { label: 'id', value: 'id' },
            { label: 'reference', value: 'reference' },
            { label: 'email', value: 'email' },
            { label: 'status', value: 'status' },
        ]
    });

    protected readonly messageThreadsBuilderMessageSenderColumnsControl = new FormControl<string[]>([
        'id', 'email',
    ]);

    protected readonly createTokenRequest = new SendableRequest<ApiTokenCreateResponse, StoreApiTokenRequest>({
        request: payload => this._settingsService.createApiToken(payload!),

        onSend: () => {
            this.createdToken = null;
            this.createTokenFormGroup.disable();
        },

        onSuccess: (result) => {
            this.createdToken = result.data.token;
            this.createTokenFormGroup.enable();

            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'API token created successfully.'
            });
        },

        onError: () => {
            this.createTokenFormGroup.enable();
        }
    });

    constructor() {
        effect(() => {
            const isAdministrator = this.user()?.permissions?.permission_name === 'Administrator';

            if (this.user() && !isAdministrator) {
                this._router.navigate(['/settings/general-information']);
            }
        });

        this._settingsService
            .listExternalRoles()
            .pipe(
                map(response => response.data ?? [])
            )
            .subscribe({
                next: (roles) => {
                    this.builderRoleDataSource = new ArrayDataSource({
                        items: roles.map(role => ({
                            label: role.name,
                            value: role.name,
                        }))
                    });

                    this.usersBuilderRolesControl.setValue(roles.map(role => role.name), { emitEvent: false });
                },
            });
    }

    protected get effectivePreviewToken(): string {
        return this.previewToken.trim() || this.createdToken || '';
    }

    protected async runPreview(key: string, url: string): Promise<void> {
        const token = this.effectivePreviewToken;
        if (!token) {
            this._snackBar.open({
                colour: 'error',
                icon: 'error',
                title: 'Enter an API token to run a preview.'
            });
            return;
        }

        this.previewResults = { ...this.previewResults, [key]: { loading: true } };

        const start = Date.now();
        try {
            // Set via the Headers API rather than an object literal: HTTP header names are
            // PascalCase, which naming-convention rejects as a property name, and the
            // ['Authorization'] form it was written in to dodge that trips
            // no-useless-computed-key instead.
            const headers = new Headers();
            headers.set('Authorization', `Bearer ${token}`);
            headers.set('Accept', 'application/json');

            const response = await fetch(url, { headers });
            const duration = Date.now() - start;
            const json = await response.json();
            this.previewResults = {
                ...this.previewResults,
                [key]: { loading: false, status: response.status, body: JSON.stringify(json, null, 2), duration }
            };
        } catch (e) {
            this.previewResults = {
                ...this.previewResults,
                [key]: { loading: false, error: 'Request failed: ' + String(e), duration: Date.now() - start }
            };
        }
    }

    protected createToken(): void {
        if (this.createTokenRequest.loading()) {
            return;
        }

        if (!this.createTokenFormGroup.valid) {
            this._formValidationService.markFormGroupTouched(this.createTokenFormGroup);
            this._formValidationService.showFormErrors(this.createTokenFormGroup, true);

            return;
        }

        this.createTokenRequest.send({
            name: this.createTokenFormGroup.value.name ?? ''
        });
    }

    protected async copyToken(token: string): Promise<void> {
        await this.copyToClipboard(token, 'Token copied to clipboard.');
    }

    protected get providersUrl(): string {
        const params = new URLSearchParams();

        if (this.builderName.trim()) {
            params.set('filter[name]', this.builderName.trim());
        }

        if (this.builderReference.trim()) {
            params.set('filter[reference]', this.builderReference.trim());
        }

        if (this.builderEmail.trim()) {
            params.set('filter[email]', this.builderEmail.trim());
        }

        if ((this.builderStatusControl.value ?? '').trim()) {
            params.set('filter[status]', (this.builderStatusControl.value ?? '').trim());
        }

        if (this.builderQuery.trim()) {
            params.set('filter[q]', this.builderQuery.trim());
        }

        const selectedRoles = this.builderRolesControl.value ?? [];
        if (selectedRoles.length > 0) {
            params.set('filter[roles]', selectedRoles.join(','));
        }

        const selectedColumns = this.builderColumnsControl.value ?? [];
        if (selectedColumns.length > 0) {
            params.set('columns', selectedColumns.join(','));
        }

        const perPage = Math.min(Math.max(Number(this.builderPerPage) || 100, 1), 500);
        params.set('per_page', String(perPage));

        const sortField = (this.builderSortControl.value ?? '').trim() || 'id';
        const sortDirectionPrefix = (this.builderSortDirectionControl.value ?? 'asc') === 'desc' ? '-' : '';
        params.set('sort', `${sortDirectionPrefix}${sortField}`);

        const includes: string[] = [];

        if (this.builderIncludeRegions) {
            includes.push('regions');
        }

        if (this.builderIncludeZones) {
            includes.push('zones');
        }

        if (this.builderIncludePriorities) {
            includes.push('priorities');
        }

        if (includes.length > 0) {
            params.set('include', includes.join(','));
        }

        const selectedRegionColumns = this.builderRegionColumnsControl.value ?? [];
        if (selectedRegionColumns.length > 0) {
            params.set('columns_regions', selectedRegionColumns.join(','));
        }

        const selectedZoneColumns = this.builderZoneColumnsControl.value ?? [];
        if (selectedZoneColumns.length > 0) {
            params.set('columns_zones', selectedZoneColumns.join(','));
        }

        const selectedPriorityColumns = this.builderPriorityColumnsControl.value ?? [];
        if (selectedPriorityColumns.length > 0) {
            params.set('columns_priorities', selectedPriorityColumns.join(','));
        }

        const queryString = params.toString();
        const baseUrl = `${globalThis.location.origin}/api/external/providers`;

        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    protected async copyProvidersUrl(): Promise<void> {
        await this.copyToClipboard(this.providersUrl, 'Providers URL copied to clipboard.');
    }

    protected get permissionTypesUrl(): string {
        const params = new URLSearchParams();

        if (this.permissionBuilderName.trim()) {
            params.set('filter[name]', this.permissionBuilderName.trim());
        }

        if (this.permissionBuilderKey.trim()) {
            params.set('filter[key]', this.permissionBuilderKey.trim());
        }

        if (this.permissionBuilderQuery.trim()) {
            params.set('filter[q]', this.permissionBuilderQuery.trim());
        }

        const queryString = params.toString();
        const baseUrl = `${globalThis.location.origin}/api/external/roles`;

        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    protected async copyPermissionTypesUrl(): Promise<void> {
        await this.copyToClipboard(this.permissionTypesUrl, 'Permissions URL copied to clipboard.');
    }

    protected get usersUrl(): string {
        const params = new URLSearchParams();

        if (this.usersBuilderReference.trim()) {
            params.set('filter[reference]', this.usersBuilderReference.trim());
        }

        if (this.usersBuilderEmail.trim()) {
            params.set('filter[email]', this.usersBuilderEmail.trim());
        }

        if ((this.usersBuilderStatusControl.value ?? '').trim()) {
            params.set('filter[status]', (this.usersBuilderStatusControl.value ?? '').trim());
        }

        if (this.usersBuilderQuery.trim()) {
            params.set('filter[q]', this.usersBuilderQuery.trim());
        }

        const selectedRoles = this.usersBuilderRolesControl.value ?? [];
        if (selectedRoles.length > 0) {
            params.set('filter[roles]', selectedRoles.join(','));
        }

        const selectedColumns = this.usersBuilderColumnsControl.value ?? [];
        if (selectedColumns.length > 0) {
            params.set('columns', selectedColumns.join(','));
        }

        const perPage = Math.min(Math.max(Number(this.usersBuilderPerPage) || 100, 1), 500);
        params.set('per_page', String(perPage));

        const sortField = (this.usersBuilderSortControl.value ?? '').trim() || 'id';
        const sortDirectionPrefix = (this.usersBuilderSortDirectionControl.value ?? 'asc') === 'desc' ? '-' : '';
        params.set('sort', `${sortDirectionPrefix}${sortField}`);

        const queryString = params.toString();
        const baseUrl = `${globalThis.location.origin}/api/external/users`;

        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    protected async copyUsersUrl(): Promise<void> {
        await this.copyToClipboard(this.usersUrl, 'Users URL copied to clipboard.');
    }

    protected get commissionersUrl(): string {
        const params = new URLSearchParams();

        if (this.commissionersBuilderName.trim()) {
            params.set('filter[name]', this.commissionersBuilderName.trim());
        }

        if (this.commissionersBuilderReference.trim()) {
            params.set('filter[reference]', this.commissionersBuilderReference.trim());
        }

        if (this.commissionersBuilderEmail.trim()) {
            params.set('filter[email]', this.commissionersBuilderEmail.trim());
        }

        if ((this.commissionersBuilderStatusControl.value ?? '').trim()) {
            params.set('filter[status]', (this.commissionersBuilderStatusControl.value ?? '').trim());
        }

        if (this.commissionersBuilderQuery.trim()) {
            params.set('filter[q]', this.commissionersBuilderQuery.trim());
        }

        const selectedColumns = this.commissionersBuilderColumnsControl.value ?? [];
        if (selectedColumns.length > 0) {
            params.set('columns', selectedColumns.join(','));
        }

        const perPage = Math.min(Math.max(Number(this.commissionersBuilderPerPage) || 100, 1), 500);
        params.set('per_page', String(perPage));

        const sortField = (this.commissionersBuilderSortControl.value ?? '').trim() || 'id';
        const sortDirectionPrefix = (this.commissionersBuilderSortDirectionControl.value ?? 'asc') === 'desc' ? '-' : '';
        params.set('sort', `${sortDirectionPrefix}${sortField}`);

        const includes: string[] = [];

        if (this.commissionersBuilderIncludeAddress) {
            includes.push('address');
        }

        if (this.commissionersBuilderIncludeUsers) {
            includes.push('users');
        }

        if (includes.length > 0) {
            params.set('include', includes.join(','));
        }

        const selectedAddressColumns = this.commissionersBuilderAddressColumnsControl.value ?? [];
        if (selectedAddressColumns.length > 0) {
            params.set('columns_address', selectedAddressColumns.join(','));
        }

        const selectedUsersColumns = this.commissionersBuilderUsersColumnsControl.value ?? [];
        if (selectedUsersColumns.length > 0) {
            params.set('columns_users', selectedUsersColumns.join(','));
        }

        const queryString = params.toString();
        const baseUrl = `${globalThis.location.origin}/api/external/commissioners`;

        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    protected async copyCommissionersUrl(): Promise<void> {
        await this.copyToClipboard(this.commissionersUrl, 'Commissioners URL copied to clipboard.');
    }

    protected get clientsUrl(): string {
        const params = new URLSearchParams();

        if (this.clientsBuilderReference.trim()) {
            params.set('filter[reference]', this.clientsBuilderReference.trim());
        }

        if (this.clientsBuilderEmail.trim()) {
            params.set('filter[email]', this.clientsBuilderEmail.trim());
        }

        if (this.clientsBuilderNhsNumber.trim()) {
            params.set('filter[nhs_number]', this.clientsBuilderNhsNumber.trim());
        }

        if ((this.clientsBuilderStatusControl.value ?? '').trim()) {
            params.set('filter[status]', (this.clientsBuilderStatusControl.value ?? '').trim());
        }

        if (this.clientsBuilderQuery.trim()) {
            params.set('filter[q]', this.clientsBuilderQuery.trim());
        }

        if (this.clientsBuilderCommissionerId.trim()) {
            params.set('filter[commissioners.id]', this.clientsBuilderCommissionerId.trim());
        }

        const selectedColumns = this.clientsBuilderColumnsControl.value ?? [];
        if (selectedColumns.length > 0) {
            params.set('columns', selectedColumns.join(','));
        }

        const perPage = Math.min(Math.max(Number(this.clientsBuilderPerPage) || 100, 1), 500);
        params.set('per_page', String(perPage));

        const sortField = (this.clientsBuilderSortControl.value ?? '').trim() || 'id';
        const sortDirectionPrefix = (this.clientsBuilderSortDirectionControl.value ?? 'asc') === 'desc' ? '-' : '';
        params.set('sort', `${sortDirectionPrefix}${sortField}`);

        const includes: string[] = [];

        if (this.clientsBuilderIncludePerson) {
            includes.push('person');
        }

        if (this.clientsBuilderIncludePersonAddress) {
            includes.push('person.address');
        }

        if (this.clientsBuilderIncludeAssociatedCommissioners) {
            includes.push('commissioners');
        }

        if (includes.length > 0) {
            params.set('include', includes.join(','));
        }

        const selectedPersonColumns = this.clientsBuilderPersonColumnsControl.value ?? [];
        if (selectedPersonColumns.length > 0) {
            params.set('columns_person', selectedPersonColumns.join(','));
        }

        const selectedAddressColumns = this.clientsBuilderAddressColumnsControl.value ?? [];
        if (selectedAddressColumns.length > 0) {
            params.set('columns_address', selectedAddressColumns.join(','));
        }

        const selectedCommissionerColumns = this.clientsBuilderCommissionersColumnsControl.value ?? [];
        if (selectedCommissionerColumns.length > 0) {
            params.set('columns_commissioners', selectedCommissionerColumns.join(','));
        }

        const queryString = params.toString();
        const baseUrl = `${globalThis.location.origin}/api/external/clients`;

        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    protected async copyClientsUrl(): Promise<void> {
        await this.copyToClipboard(this.clientsUrl, 'Clients URL copied to clipboard.');
    }

    protected get regionsUrl(): string {
        const params = new URLSearchParams();

        if (this.regionsBuilderName.trim()) {
            params.set('filter[name]', this.regionsBuilderName.trim());
        }

        if (this.regionsBuilderReference.trim()) {
            params.set('filter[reference]', this.regionsBuilderReference.trim());
        }

        if ((this.regionsBuilderStatusControl.value ?? '').trim()) {
            params.set('filter[status]', (this.regionsBuilderStatusControl.value ?? '').trim());
        }

        if (this.regionsBuilderQuery.trim()) {
            params.set('filter[q]', this.regionsBuilderQuery.trim());
        }

        const selectedColumns = this.regionsBuilderColumnsControl.value ?? [];
        if (selectedColumns.length > 0) {
            params.set('columns', selectedColumns.join(','));
        }

        const perPage = Math.min(Math.max(Number(this.regionsBuilderPerPage) || 100, 1), 500);
        params.set('per_page', String(perPage));

        const sortField = (this.regionsBuilderSortControl.value ?? '').trim() || 'name';
        const sortDirectionPrefix = (this.regionsBuilderSortDirectionControl.value ?? 'asc') === 'desc' ? '-' : '';
        params.set('sort', `${sortDirectionPrefix}${sortField}`);

        const selectedPriorityColumns = this.regionsBuilderPriorityColumnsControl.value ?? [];
        if (selectedPriorityColumns.length > 0) {
            params.set('columns_priorities', selectedPriorityColumns.join(','));
        }

        const selectedZoneColumns = this.regionsBuilderZoneColumnsControl.value ?? [];
        if (selectedZoneColumns.length > 0) {
            params.set('columns_zones', selectedZoneColumns.join(','));
        }

        if (this.regionsBuilderIncludeProviders) {
            const includes = ['providers'];
            params.set('include', includes.join(','));

            const cols = this.regionsBuilderProviderColumnsControl.value ?? [];
            if (cols.length > 0) {
                params.set('columns_providers', cols.join(','));
            }
        }

        const queryString = params.toString();
        const baseUrl = `${globalThis.location.origin}/api/external/regions`;

        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    protected async copyRegionsUrl(): Promise<void> {
        await this.copyToClipboard(this.regionsUrl, 'Regions URL copied to clipboard.');
    }

    protected get careTypesUrl(): string {
        const params = new URLSearchParams();

        if ((this.careTypesBuilderStatusControl.value ?? '').trim()) {
            params.set('filter[status]', (this.careTypesBuilderStatusControl.value ?? '').trim());
        }

        if (this.careTypesBuilderQuery.trim()) {
            params.set('filter[q]', this.careTypesBuilderQuery.trim());
        }

        const selectedColumns = this.careTypesBuilderColumnsControl.value ?? [];
        if (selectedColumns.length > 0) {
            params.set('columns', selectedColumns.join(','));
        }

        const perPage = Math.min(Math.max(Number(this.careTypesBuilderPerPage) || 100, 1), 500);
        params.set('per_page', String(perPage));

        const sortField = (this.careTypesBuilderSortControl.value ?? '').trim() || 'name';
        const sortDirectionPrefix = (this.careTypesBuilderSortDirectionControl.value ?? 'asc') === 'desc' ? '-' : '';
        params.set('sort', `${sortDirectionPrefix}${sortField}`);

        if (this.careTypesBuilderIncludeCareOptions) {
            params.set('include', 'careOptions');

            const cols = this.careTypesBuilderCareOptionColumnsControl.value ?? [];
            if (cols.length > 0) {
                params.set('columns_care_options', cols.join(','));
            }
        }

        const queryString = params.toString();
        const baseUrl = `${globalThis.location.origin}/api/external/care-types`;

        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    protected async copyCareTypesUrl(): Promise<void> {
        await this.copyToClipboard(this.careTypesUrl, 'Care types URL copied to clipboard.');
    }

    protected get careOptionsUrl(): string {
        const params = new URLSearchParams();

        if (this.careOptionsBuilderCareTypeId.trim()) {
            params.set('filter[care_type_id]', this.careOptionsBuilderCareTypeId.trim());
        }

        if ((this.careOptionsBuilderStatusControl.value ?? '').trim()) {
            params.set('filter[status]', (this.careOptionsBuilderStatusControl.value ?? '').trim());
        }

        if ((this.careOptionsBuilderIncludePriceControl.value ?? '').trim()) {
            params.set('filter[include_price]', (this.careOptionsBuilderIncludePriceControl.value ?? '').trim());
        }

        if (this.careOptionsBuilderQuery.trim()) {
            params.set('filter[q]', this.careOptionsBuilderQuery.trim());
        }

        const selectedColumns = this.careOptionsBuilderColumnsControl.value ?? [];
        if (selectedColumns.length > 0) {
            params.set('columns', selectedColumns.join(','));
        }

        const perPage = Math.min(Math.max(Number(this.careOptionsBuilderPerPage) || 100, 1), 500);
        params.set('per_page', String(perPage));

        const sortField = (this.careOptionsBuilderSortControl.value ?? '').trim() || 'name';
        const sortDirectionPrefix = (this.careOptionsBuilderSortDirectionControl.value ?? 'asc') === 'desc' ? '-' : '';
        params.set('sort', `${sortDirectionPrefix}${sortField}`);

        const selectedCareTypeColumns = this.careOptionsBuilderCareTypeColumnsControl.value ?? [];
        if (selectedCareTypeColumns.length > 0) {
            params.set('columns_care_type', selectedCareTypeColumns.join(','));
        }

        const queryString = params.toString();
        const baseUrl = `${globalThis.location.origin}/api/external/care-options`;

        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    protected async copyCareOptionsUrl(): Promise<void> {
        await this.copyToClipboard(this.careOptionsUrl, 'Care options URL copied to clipboard.');
    }

    protected get handbacksUrl(): string {
        const params = new URLSearchParams();

        if (this.handbacksBuilderPackageId.trim()) {
            params.set('filter[package_id]', this.handbacksBuilderPackageId.trim());
        }

        if (this.handbacksBuilderProviderId.trim()) {
            params.set('filter[provider_id]', this.handbacksBuilderProviderId.trim());
        }

        if (this.handbacksBuilderHandbackReasonId.trim()) {
            params.set('filter[handback_reason_id]', this.handbacksBuilderHandbackReasonId.trim());
        }

        if ((this.handbacksBuilderPrivateControl.value ?? '').trim()) {
            params.set('filter[private]', (this.handbacksBuilderPrivateControl.value ?? '').trim());
        }

        if (this.handbacksBuilderQuery.trim()) {
            params.set('filter[q]', this.handbacksBuilderQuery.trim());
        }

        const selectedColumns = this.handbacksBuilderColumnsControl.value ?? [];
        if (selectedColumns.length > 0) {
            params.set('columns', selectedColumns.join(','));
        }

        const perPage = Math.min(Math.max(Number(this.handbacksBuilderPerPage) || 100, 1), 500);
        params.set('per_page', String(perPage));

        const sortField = (this.handbacksBuilderSortControl.value ?? '').trim() || 'id';
        const sortDirectionPrefix = (this.handbacksBuilderSortDirectionControl.value ?? 'asc') === 'desc' ? '-' : '';
        params.set('sort', `${sortDirectionPrefix}${sortField}`);

        if (this.handbacksBuilderIncludePackage) {
            params.set('include', 'package');

            const cols = this.handbacksBuilderPackageColumnsControl.value ?? [];
            if (cols.length > 0) {
                params.set('columns_package', cols.join(','));
            }
        }

        const queryString = params.toString();
        const baseUrl = `${globalThis.location.origin}/api/external/handbacks`;

        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    protected async copyHandbacksUrl(): Promise<void> {
        await this.copyToClipboard(this.handbacksUrl, 'Handbacks URL copied to clipboard.');
    }

    protected get packagesUrl(): string {
        const params = new URLSearchParams();

        if (this.packagesBuilderReference.trim()) {
            params.set('filter[reference]', this.packagesBuilderReference.trim());
        }

        if (this.packagesBuilderCommissionerId.trim()) {
            params.set('filter[commissioner_id]', this.packagesBuilderCommissionerId.trim());
        }

        if (this.packagesBuilderProviderId.trim()) {
            params.set('filter[provider_id]', this.packagesBuilderProviderId.trim());
        }

        if (this.packagesBuilderCareOptionId.trim()) {
            params.set('filter[care_option_id]', this.packagesBuilderCareOptionId.trim());
        }

        if ((this.packagesBuilderOpenControl.value ?? '').trim()) {
            params.set('filter[open]', (this.packagesBuilderOpenControl.value ?? '').trim());
        }

        if ((this.packagesBuilderPublishControl.value ?? '').trim()) {
            params.set('filter[publish]', (this.packagesBuilderPublishControl.value ?? '').trim());
        }

        if (this.packagesBuilderQuery.trim()) {
            params.set('filter[q]', this.packagesBuilderQuery.trim());
        }

        const selectedColumns = this.packagesBuilderColumnsControl.value ?? [];
        if (selectedColumns.length > 0) {
            params.set('columns', selectedColumns.join(','));
        }

        const perPage = Math.min(Math.max(Number(this.packagesBuilderPerPage) || 100, 1), 500);
        params.set('per_page', String(perPage));

        const sortField = (this.packagesBuilderSortControl.value ?? '').trim() || 'id';
        const sortDirectionPrefix = (this.packagesBuilderSortDirectionControl.value ?? 'asc') === 'desc' ? '-' : '';
        params.set('sort', `${sortDirectionPrefix}${sortField}`);

        const includes: string[] = [];

        if (this.packagesBuilderIncludeAddress) {
            includes.push('address');
        }

        if (this.packagesBuilderIncludeClients) {
            includes.push('clients');
        }

        if (this.packagesBuilderIncludeCommissioner) {
            includes.push('commissioner');
        }

        if (this.packagesBuilderIncludeProvider) {
            includes.push('provider');
        }

        if (this.packagesBuilderIncludeCareOption) {
            includes.push('careOption');
        }

        if (includes.length > 0) {
            params.set('include', includes.join(','));
        }

        if (this.packagesBuilderIncludeAddress) {
            const cols = this.packagesBuilderAddressColumnsControl.value ?? [];
            if (cols.length > 0) {
                params.set('columns_address', cols.join(','));
            }
        }

        if (this.packagesBuilderIncludeClients) {
            const cols = this.packagesBuilderClientsColumnsControl.value ?? [];
            if (cols.length > 0) {
                params.set('columns_clients', cols.join(','));
            }
        }

        if (this.packagesBuilderIncludeCommissioner) {
            const cols = this.packagesBuilderCommissionerColumnsControl.value ?? [];
            if (cols.length > 0) {
                params.set('columns_commissioner', cols.join(','));
            }
        }

        if (this.packagesBuilderIncludeProvider) {
            const cols = this.packagesBuilderProviderColumnsControl.value ?? [];
            if (cols.length > 0) {
                params.set('columns_provider', cols.join(','));
            }
        }

        if (this.packagesBuilderIncludeCareOption) {
            const cols = this.packagesBuilderCareOptionColumnsControl.value ?? [];
            if (cols.length > 0) {
                params.set('columns_care_option', cols.join(','));
            }
        }

        const queryString = params.toString();
        const baseUrl = `${globalThis.location.origin}/api/external/packages`;

        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    protected async copyPackagesUrl(): Promise<void> {
        await this.copyToClipboard(this.packagesUrl, 'Packages URL copied to clipboard.');
    }

    protected get messageThreadsUrl(): string {
        const params = new URLSearchParams();

        if (this.messageThreadsBuilderSenderId.trim()) {
            params.set('filter[sender_id]', this.messageThreadsBuilderSenderId.trim());
        }

        if (this.messageThreadsBuilderSubject.trim()) {
            params.set('filter[subject]', this.messageThreadsBuilderSubject.trim());
        }

        const selectedColumns = this.messageThreadsBuilderColumnsControl.value ?? [];
        if (selectedColumns.length > 0) {
            params.set('columns', selectedColumns.join(','));
        }

        const selectedSenderColumns = this.messageThreadsBuilderSenderColumnsControl.value ?? [];
        if (selectedSenderColumns.length > 0) {
            params.set('columns_sender', selectedSenderColumns.join(','));
        }

        const selectedMessageColumns = this.messageThreadsBuilderMessageColumnsControl.value ?? [];
        if (selectedMessageColumns.length > 0) {
            params.set('columns_messages', selectedMessageColumns.join(','));
        }

        const selectedMessageSenderColumns = this.messageThreadsBuilderMessageSenderColumnsControl.value ?? [];
        if (selectedMessageSenderColumns.length > 0) {
            params.set('columns_messages_sender', selectedMessageSenderColumns.join(','));
        }

        const perPage = Math.min(Math.max(Number(this.messageThreadsBuilderPerPage) || 100, 1), 500);
        params.set('per_page', String(perPage));

        const sortField = (this.messageThreadsBuilderSortControl.value ?? '').trim() || 'created_at';
        const sortDirectionPrefix = (this.messageThreadsBuilderSortDirectionControl.value ?? 'desc') === 'desc' ? '-' : '';
        params.set('sort', `${sortDirectionPrefix}${sortField}`);

        const queryString = params.toString();
        const baseUrl = `${globalThis.location.origin}/api/external/message-threads`;

        return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }

    protected async copyMessageThreadsUrl(): Promise<void> {
        await this.copyToClipboard(this.messageThreadsUrl, 'Message threads URL copied to clipboard.');
    }

    private async copyToClipboard(value: string, successMessage: string): Promise<void> {
        try {
            await navigator.clipboard.writeText(value);

            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: successMessage
            });
        } catch {
            this._snackBar.open({
                colour: 'error',
                icon: 'error',
                title: 'Failed to copy to clipboard.'
            });
        }
    }
}
